/**
 * InGameChat — in-match public text chat for players who'd rather type than
 * use the mic. Three surfaces, one source of truth (`this.messages`):
 *
 *  1. Compose bar  — bottom-anchored free-text input + one-tap street phrases,
 *                    opened from the green chat badge on the local avatar.
 *  2. Bubbles      — per-player speech bubbles anchored to each profile card
 *                    (left/right by slot, tail arrow), capped to the card's
 *                    height so they never blanket the table, auto-scrolling
 *                    long text (manual scroll supported).
 *  3. History modal — full scrollable transcript (avatar + name + time) for the
 *                    'Match Chat' menu item, sized for a screenshot/report.
 *
 * Network model (see CLAUDE.md architecture invariant): live lines arrive via
 * the `game:chat_message` relay; the recent buffer rides `state:full` as
 * `recentChat`, so a player who joins late or resumes from background sees the
 * recent conversation. We merge both into `messages`, deduped by id. Avatar +
 * username are resolved from the live roster, not carried per message.
 */

import gsap from 'gsap';
import type { NetworkManager } from '../../../network/NetworkManager';
import type { PlayerProfilesOverlay, ProfileSlot } from '../PlayerProfilesOverlay';
import type { GameChatMessage } from '@sdl/shared';

export interface InGameChatOptions {
  container: HTMLElement;
  networkManager: NetworkManager;
  profiles: PlayerProfilesOverlay;
  getLocalPlayerId: () => string | null;
}

/** One-tap street-dice phrases + emotes for fast chatter mid-throw. */
const QUICK_CHIPS = ['Bet!', 'Fade that', 'Set it', 'Bank it', 'Lil 7', 'GG', '🎲', '🔥', '💵', '😤'];

const MAX_MESSAGES = 120;        // full transcript cap (matches server buffer ballpark)
const BUBBLE_IDLE_HIDE_MS = 9000; // hide a bubble this long after its last line
const BUBBLE_READ_DELAY_MS = 2200; // let the reader take in the visible top before auto-panning
const MAX_LEN = 200;             // mirrors server RoomChat MAX_LEN
/** Constant width cap for the top (opponent) bubbles. Deriving the width from
 *  the card's inner edge made the bubble shrink to a sliver whenever sidebet
 *  chips / result icons widened the card — opponent bubbles must stay a fixed,
 *  readable width regardless of card contents. */
const OPPONENT_BUBBLE_MAX_W = 150;

interface PlayerBubble {
  root: HTMLElement;
  scroll: HTMLElement;
  hideTimer: number | null;
  autoPanTween: gsap.core.Tween | null;
  autoPanDelay: number | null;
  manualScroll: boolean;
}

export class InGameChat {
  private container: HTMLElement;
  private nm: NetworkManager;
  private profiles: PlayerProfilesOverlay;
  private getLocalPlayerId: () => string | null;

  private messages: GameChatMessage[] = [];
  private seenIds = new Set<string>();

  // Compose bar
  private composeEl: HTMLElement;
  private composeInput!: HTMLTextAreaElement;
  private composeOpen = false;

  // Bubbles
  private bubbleLayer: HTMLElement;
  private bubbles = new Map<string, PlayerBubble>();
  // Profile cards are CSS-anchored to screen corners (they don't move with the
  // camera), so we don't track them every frame. We re-anchor on a cheap low-
  // frequency tick (covers roster reshuffles) + on resize, and re-measure width
  // only on show/resize (the querySelector + layout reads are the costly part).
  private repositionTimer: number | null = null;

  // History modal
  private historyEl: HTMLElement | null = null;

  private onMessageBound = (m: GameChatMessage) => this.handleMessage(m);
  private onStateFullBound = (s: { recentChat?: GameChatMessage[] }) => this.hydrate(s.recentChat);
  private onResizeBound = () => this.relayoutAll();

  constructor(opts: InGameChatOptions) {
    this.container = opts.container;
    this.nm = opts.networkManager;
    this.profiles = opts.profiles;
    this.getLocalPlayerId = opts.getLocalPlayerId;

    InGameChat.injectStyles();

    this.bubbleLayer = document.createElement('div');
    this.bubbleLayer.className = 'igc-bubble-layer';
    this.container.appendChild(this.bubbleLayer);

    this.composeEl = this.buildComposeBar();
    this.container.appendChild(this.composeEl);

    this.profiles.on('chat:open', this.toggleCompose);
    this.nm.on('game:chat_message', this.onMessageBound);
    this.nm.on('state:full', this.onStateFullBound);
    window.addEventListener('resize', this.onResizeBound);
  }

  destroy(): void {
    this.profiles.off('chat:open', this.toggleCompose);
    this.nm.off('game:chat_message', this.onMessageBound);
    this.nm.off('state:full', this.onStateFullBound);
    window.removeEventListener('resize', this.onResizeBound);
    if (this.repositionTimer != null) clearInterval(this.repositionTimer);
    this.bubbles.forEach((b) => {
      if (b.hideTimer != null) clearTimeout(b.hideTimer);
      this.stopAutoPan(b);
    });
    this.bubbles.clear();
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.syncKeyboardInset);
      window.visualViewport.removeEventListener('scroll', this.syncKeyboardInset);
    }
    this.bubbleLayer.remove();
    this.composeEl.remove();
    this.historyEl?.remove();
  }

  // ---------- Compose bar ----------

  private toggleCompose = (): void => {
    if (this.composeOpen) this.closeCompose();
    else this.openCompose();
  };

  openCompose(): void {
    this.composeOpen = true;
    this.composeEl.classList.add('open');
    this.profiles.setChatUnread(false);
    // Track the on-screen keyboard so the bar rides above it (see syncKeyboardInset).
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.syncKeyboardInset);
      window.visualViewport.addEventListener('scroll', this.syncKeyboardInset);
    }
    // Defer focus so the slide-in isn't fought by the keyboard on mobile.
    requestAnimationFrame(() => this.composeInput.focus());
  }

  closeCompose(): void {
    this.composeOpen = false;
    this.composeEl.classList.remove('open');
    this.composeInput.blur();
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.syncKeyboardInset);
      window.visualViewport.removeEventListener('scroll', this.syncKeyboardInset);
    }
    this.composeEl.style.bottom = '';
  }

  /**
   * Lift the compose bar to sit on top of the soft keyboard. We position from
   * the visual viewport (not layout), so this is correct everywhere: in a
   * mobile browser the keyboard overlays and shrinks the visual viewport (we
   * lift by the keyboard height); under Capacitor's default `native` resize the
   * webview itself shrinks, so the overlap computes to ~0 and we don't
   * double-shift. Avoids resizing the full-screen Three.js canvas.
   */
  private syncKeyboardInset = (): void => {
    const vv = window.visualViewport;
    if (!vv) return;
    const overlap = window.innerHeight - (vv.height + vv.offsetTop);
    this.composeEl.style.bottom = `${Math.max(0, Math.round(overlap))}px`;
  };

  private send(text: string): void {
    const trimmed = text.trim().slice(0, MAX_LEN);
    if (!trimmed) return;
    this.nm.sendGameChat(trimmed);
    this.composeInput.value = '';
    this.autoSizeInput();
    // Dismiss the bar + keyboard after sending so the table is clear again.
    this.closeCompose();
  }

  private buildComposeBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'igc-compose';

    const chips = document.createElement('div');
    chips.className = 'igc-chips';
    QUICK_CHIPS.forEach((label) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'igc-chip';
      chip.textContent = label;
      chip.addEventListener('click', () => this.send(label));
      chips.appendChild(chip);
    });
    bar.appendChild(chips);

    const row = document.createElement('div');
    row.className = 'igc-input-row';

    const input = document.createElement('textarea');
    input.className = 'igc-input';
    input.rows = 1;
    input.maxLength = MAX_LEN;
    input.placeholder = 'Say something...';
    input.addEventListener('input', () => this.autoSizeInput());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.send(input.value);
      }
    });
    this.composeInput = input;
    row.appendChild(input);

    const sendBtn = document.createElement('button');
    sendBtn.type = 'button';
    sendBtn.className = 'igc-send';
    sendBtn.setAttribute('aria-label', 'Send');
    sendBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>`;
    sendBtn.addEventListener('click', () => this.send(input.value));
    row.appendChild(sendBtn);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'igc-close';
    closeBtn.setAttribute('aria-label', 'Close chat');
    closeBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>`;
    closeBtn.addEventListener('click', () => this.closeCompose());
    row.appendChild(closeBtn);

    bar.appendChild(row);
    return bar;
  }

  private autoSizeInput(): void {
    const el = this.composeInput;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }

  // ---------- Message handling ----------

  private handleMessage(msg: GameChatMessage): void {
    if (this.seenIds.has(msg.id)) return;
    this.seenIds.add(msg.id);
    this.messages.push(msg);
    if (this.messages.length > MAX_MESSAGES) {
      const dropped = this.messages.shift();
      if (dropped) this.seenIds.delete(dropped.id);
    }
    this.showBubble(msg);
    this.appendToHistory(msg);
    if (!this.composeOpen && msg.playerId !== this.getLocalPlayerId()) {
      this.profiles.setChatUnread(true);
    }
  }

  /** Merge a resume snapshot's recent chat without disturbing live ordering. */
  private hydrate(recent?: GameChatMessage[]): void {
    if (!recent?.length) return;
    let added = false;
    for (const msg of recent) {
      if (this.seenIds.has(msg.id)) continue;
      this.seenIds.add(msg.id);
      this.messages.push(msg);
      added = true;
    }
    if (!added) return;
    // Keep transcript ordered by time after a merge (snapshot may predate live).
    this.messages.sort((a, b) => a.timestamp - b.timestamp);
    if (this.messages.length > MAX_MESSAGES) {
      this.messages = this.messages.slice(-MAX_MESSAGES);
      this.seenIds = new Set(this.messages.map((m) => m.id));
    }
    if (this.historyEl) this.rebuildHistory();
  }

  // ---------- Bubbles ----------

  private showBubble(msg: GameChatMessage): void {
    const card = this.profiles.getCardElement(msg.playerId);
    if (!card) return; // player not on screen (left) — transcript still has it
    const slot = this.profiles.getCardSlot(msg.playerId) ?? 'bottomRow';

    let bubble = this.bubbles.get(msg.playerId);
    if (!bubble) bubble = this.createBubble(msg.playerId);

    // Bubble shows only the latest line — replace, don't accumulate. (The full
    // transcript lives in the Match Chat history modal.)
    bubble.scroll.replaceChildren();
    const line = document.createElement('div');
    line.className = 'igc-line';
    line.textContent = msg.text;
    bubble.scroll.appendChild(line);

    bubble.root.dataset.slot = slot;
    bubble.root.classList.add('visible');
    bubble.manualScroll = false;
    this.measureBubble(bubble, card, slot); // width + height: the costly reads, done once per message
    this.anchorBubble(bubble, card, slot);
    this.revealLatest(bubble, line);

    if (bubble.hideTimer != null) clearTimeout(bubble.hideTimer);
    bubble.hideTimer = window.setTimeout(() => this.hideBubble(msg.playerId), BUBBLE_IDLE_HIDE_MS);

    this.ensureRepositionLoop();
  }

  private createBubble(playerId: string): PlayerBubble {
    const root = document.createElement('div');
    root.className = 'igc-bubble';

    const scroll = document.createElement('div');
    scroll.className = 'igc-bubble__scroll';
    // Manual scroll cancels auto-pan (and its pending start) — reader takes over.
    const onManual = () => { bubble.manualScroll = true; this.stopAutoPan(bubble); };
    scroll.addEventListener('wheel', onManual, { passive: true });
    scroll.addEventListener('touchstart', onManual, { passive: true });
    root.appendChild(scroll);

    const tail = document.createElement('div');
    tail.className = 'igc-bubble__tail';
    root.appendChild(tail);

    this.bubbleLayer.appendChild(root);
    const bubble: PlayerBubble = { root, scroll, hideTimer: null, autoPanTween: null, autoPanDelay: null, manualScroll: false };
    this.bubbles.set(playerId, bubble);
    return bubble;
  }

  private stopAutoPan(bubble: PlayerBubble): void {
    if (bubble.autoPanTween) { bubble.autoPanTween.kill(); bubble.autoPanTween = null; }
    if (bubble.autoPanDelay != null) { clearTimeout(bubble.autoPanDelay); bubble.autoPanDelay = null; }
  }

  private hideBubble(playerId: string): void {
    const bubble = this.bubbles.get(playerId);
    if (!bubble) return;
    bubble.root.classList.remove('visible');
    this.stopAutoPan(bubble);
  }

  /** Size the bubble: height capped to the card, width to the card's sidebet
   *  container. Does the querySelector + layout reads — call on show / resize
   *  only, NOT every frame. */
  private measureBubble(bubble: PlayerBubble, card: HTMLElement, slot: ProfileSlot): void {
    const r = card.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return; // card detached
    const maxH = Math.max(44, r.height);
    bubble.scroll.style.maxHeight = `${maxH}px`;
    bubble.root.style.maxHeight = `${maxH}px`;

    // Top (opponent) bubbles: FIXED width cap, clamped only by the screen
    // edge. Measuring from the card's inner edge to the centre line made the
    // bubble shrink whenever sidebet chips / result icons widened the card,
    // down to an unreadable sliver. A constant cap keeps it readable; the two
    // top bubbles can briefly overlap mid-screen if both opponents talk at
    // once, which reads fine (bubbles are transient and content-sized).
    // Bottom (local) bubble keeps growing right toward centre — its anchor is
    // the card's OUTER edge, so card width never affected it.
    const gap = 8;
    const edgeMargin = 8;
    const avail = slot === 'topRight'
      ? Math.min(OPPONENT_BUBBLE_MAX_W, (r.left - gap) - edgeMargin)
      : slot === 'topLeft'
        ? Math.min(OPPONENT_BUBBLE_MAX_W, window.innerWidth - (r.right + gap) - edgeMargin)
        : window.innerWidth / 2 - r.left;   // bottom card → grows right toward centre
    const maxW = Math.max(72, Math.floor(avail));
    bubble.root.style.maxWidth = `${maxW}px`;
  }

  /** Position the bubble beside the card. One card-rect read — cheap enough for
   *  the low-frequency re-anchor tick (cards are screen-anchored, rarely move). */
  private anchorBubble(bubble: PlayerBubble, card: HTMLElement, slot: ProfileSlot): void {
    const r = card.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;
    const gap = 8;
    const el = bubble.root;
    el.style.left = el.style.right = el.style.top = el.style.bottom = 'auto';
    if (slot === 'topRight') {
      el.style.right = `${Math.round(window.innerWidth - r.left + gap)}px`;
      el.style.top = `${Math.round(r.top)}px`;
    } else if (slot === 'topLeft') {
      el.style.left = `${Math.round(r.right + gap)}px`;
      el.style.top = `${Math.round(r.top)}px`;
    } else {
      el.style.left = `${Math.round(r.left)}px`;
      el.style.bottom = `${Math.round(window.innerHeight - r.top + gap)}px`;
    }
  }

  /** Show the start of the newest line; if it overflows, hold a beat so the
   *  reader can take in the visible top, THEN slow auto-pan to the bottom. */
  private revealLatest(bubble: PlayerBubble, line: HTMLElement): void {
    this.stopAutoPan(bubble);
    const scroll = bubble.scroll;
    // Snap so the new line's top is in view (reading starts from its beginning).
    scroll.scrollTop = Math.max(0, line.offsetTop - 4);
    if (scroll.scrollHeight - scroll.clientHeight <= scroll.scrollTop + 2 || bubble.manualScroll) return;

    // Don't pan straight away — let the visible portion be read first.
    bubble.autoPanDelay = window.setTimeout(() => {
      bubble.autoPanDelay = null;
      this.startAutoPan(bubble);
    }, BUBBLE_READ_DELAY_MS);
  }

  private startAutoPan(bubble: PlayerBubble): void {
    const scroll = bubble.scroll;
    const start = scroll.scrollTop;
    const target = scroll.scrollHeight - scroll.clientHeight;
    const distance = target - start;
    if (distance <= 2 || bubble.manualScroll) return;
    // ~40px/sec readable pace, clamped so short overflows still feel snappy.
    const duration = Math.min(6000, Math.max(900, (distance / 40) * 1000));
    const proxy = { k: 0 };
    bubble.autoPanTween = gsap.to(proxy, {
      k: 1,
      duration: duration / 1000,
      ease: 'none', // linear readable pan
      onUpdate: () => {
        if (bubble.manualScroll) { bubble.autoPanTween?.kill(); bubble.autoPanTween = null; return; }
        scroll.scrollTop = start + distance * proxy.k;
      },
      onComplete: () => { bubble.autoPanTween = null; },
    });
  }

  /** Re-anchor visible bubbles on a slow tick (≈5Hz) so they follow a card that
   *  shifted slot when the roster changed. Anchor-only (one card-rect each); no
   *  width re-measure. Self-stops when no bubble is visible. */
  private ensureRepositionLoop(): void {
    if (this.repositionTimer != null) return;
    this.repositionTimer = window.setInterval(() => {
      let anyVisible = false;
      for (const [playerId, bubble] of this.bubbles) {
        if (!bubble.root.classList.contains('visible')) continue;
        const card = this.profiles.getCardElement(playerId);
        if (!card) { this.hideBubble(playerId); continue; }
        anyVisible = true;
        const slot = (bubble.root.dataset.slot as ProfileSlot) ?? 'bottomRow';
        this.anchorBubble(bubble, card, slot);
      }
      if (!anyVisible && this.repositionTimer != null) {
        clearInterval(this.repositionTimer);
        this.repositionTimer = null;
      }
    }, 200);
  }

  /** Full re-measure + re-anchor of every visible bubble (e.g. on resize). */
  private relayoutAll(): void {
    for (const [playerId, bubble] of this.bubbles) {
      if (!bubble.root.classList.contains('visible')) continue;
      const card = this.profiles.getCardElement(playerId);
      if (!card) { this.hideBubble(playerId); continue; }
      const slot = this.profiles.getCardSlot(playerId) ?? 'bottomRow';
      bubble.root.dataset.slot = slot;
      this.measureBubble(bubble, card, slot);
      this.anchorBubble(bubble, card, slot);
    }
  }

  // ---------- History modal ----------

  openHistory(): void {
    if (this.historyEl) { this.historyEl.classList.add('open'); return; }
    const overlay = document.createElement('div');
    overlay.className = 'igc-history';
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closeHistory(); });

    const sheet = document.createElement('div');
    sheet.className = 'igc-history__sheet';

    const header = document.createElement('div');
    header.className = 'igc-history__header';
    header.innerHTML = `<span class="igc-history__title">MATCH CHAT</span>`;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'igc-history__close';
    close.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>`;
    close.addEventListener('click', () => this.closeHistory());
    header.appendChild(close);
    sheet.appendChild(header);

    const list = document.createElement('div');
    list.className = 'igc-history__list';
    sheet.appendChild(list);

    overlay.appendChild(sheet);
    this.container.appendChild(overlay);
    this.historyEl = overlay;
    this.rebuildHistory();
    requestAnimationFrame(() => overlay.classList.add('open'));
  }

  closeHistory(): void {
    if (!this.historyEl) return;
    const el = this.historyEl;
    el.classList.remove('open');
    window.setTimeout(() => { el.remove(); if (this.historyEl === el) this.historyEl = null; }, 220);
  }

  private rebuildHistory(): void {
    const list = this.historyEl?.querySelector('.igc-history__list');
    if (!list) return;
    list.innerHTML = '';
    if (!this.messages.length) {
      const empty = document.createElement('div');
      empty.className = 'igc-history__empty';
      empty.textContent = 'No messages yet.';
      list.appendChild(empty);
      return;
    }
    for (const msg of this.messages) list.appendChild(this.historyRow(msg));
    requestAnimationFrame(() => { list.scrollTop = list.scrollHeight; });
  }

  private appendToHistory(msg: GameChatMessage): void {
    const list = this.historyEl?.querySelector('.igc-history__list');
    if (!list) return;
    const empty = list.querySelector('.igc-history__empty');
    if (empty) empty.remove();
    const nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 60;
    list.appendChild(this.historyRow(msg));
    if (nearBottom) list.scrollTop = list.scrollHeight;
  }

  private historyRow(msg: GameChatMessage): HTMLElement {
    const row = document.createElement('div');
    row.className = 'igc-history__row';
    if (msg.playerId === this.getLocalPlayerId()) row.classList.add('mine');

    const avatarUrl = this.profiles.getAvatar(msg.playerId);
    const avatar = document.createElement('div');
    avatar.className = 'igc-history__avatar';
    if (avatarUrl) {
      const img = document.createElement('img');
      img.src = avatarUrl;
      img.alt = '';
      img.onerror = () => { avatar.textContent = (msg.username[0] ?? '?').toUpperCase(); };
      avatar.appendChild(img);
    } else {
      avatar.textContent = (msg.username[0] ?? '?').toUpperCase();
    }
    row.appendChild(avatar);

    const body = document.createElement('div');
    body.className = 'igc-history__body';
    const meta = document.createElement('div');
    meta.className = 'igc-history__meta';
    meta.innerHTML = `<span class="igc-history__name">${escapeHtml(msg.username)}</span><span class="igc-history__time">${formatTime(msg.timestamp)}</span>`;
    const text = document.createElement('div');
    text.className = 'igc-history__text';
    text.textContent = msg.text;
    body.appendChild(meta);
    body.appendChild(text);
    row.appendChild(body);
    return row;
  }

  // ---------- Styles ----------

  private static injectStyles(): void {
    if (document.getElementById('igc-styles')) return;
    const style = document.createElement('style');
    style.id = 'igc-styles';
    style.textContent = `
      .igc-bubble-layer {
        position: fixed; inset: 0; z-index: 60; pointer-events: none;
      }
      .igc-bubble {
        position: fixed;
        max-width: 210px;
        /* Light fill so it reads against the dark concrete wall (Phone Dice style). */
        background: #f4f4f6;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 12px;
        padding: 6px 9px;
        opacity: 0;
        transform: scale(0.92);
        transform-origin: bottom left;
        transition: opacity 0.16s ease, transform 0.16s ease;
        /* Dark drop shadow + faint light ring lifts it off any background. */
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(0, 0, 0, 0.25);
      }
      .igc-bubble.visible { opacity: 1; transform: scale(1); pointer-events: auto; }
      .igc-bubble__scroll {
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: rgba(0,0,0,0.28) transparent;
        -webkit-overflow-scrolling: touch;
      }
      .igc-bubble__scroll::-webkit-scrollbar { width: 3px; }
      .igc-bubble__scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.28); border-radius: 3px; }
      .igc-line {
        font-family: var(--font-body, 'Rajdhani', sans-serif);
        font-size: 13px;
        font-weight: 600;
        line-height: 1.25;
        color: #16161a;
        word-break: break-word;
        white-space: pre-wrap;
      }
      /* Tail = a solid triangle that always reads as part of the bubble (no
         outline seam), pointing toward the card. Side tails are vertically
         centred so they stay attached even on a tiny one-word bubble. */
      .igc-bubble__tail {
        position: absolute;
        width: 0; height: 0;
        border-style: solid;
        border-color: transparent;
      }
      .igc-bubble[data-slot="topLeft"] .igc-bubble__tail {
        left: -7px; top: 50%; transform: translateY(-50%);
        border-width: 6px 8px 6px 0; border-right-color: #f4f4f6;
      }
      .igc-bubble[data-slot="topRight"] .igc-bubble__tail {
        right: -7px; top: 50%; transform: translateY(-50%);
        border-width: 6px 0 6px 8px; border-left-color: #f4f4f6;
      }
      .igc-bubble[data-slot="bottomRow"] .igc-bubble__tail {
        left: 14px; bottom: -7px;
        border-width: 8px 6px 0 6px; border-top-color: #f4f4f6;
      }

      /* ── Compose bar ── */
      .igc-compose {
        position: fixed;
        left: 50%;
        bottom: 0;
        width: min(560px, 100%);
        transform: translate(-50%, 110%);
        transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), bottom 0.18s ease-out;
        z-index: 560;
        padding: 8px 10px calc(10px + env(safe-area-inset-bottom));
        background: linear-gradient(180deg, rgba(8,8,10,0) 0%, rgba(8,8,10,0.92) 28%);
        display: flex; flex-direction: column; gap: 8px;
        pointer-events: none;
      }
      .igc-compose.open { transform: translate(-50%, 0); pointer-events: auto; }
      .igc-chips { display: flex; gap: 6px; overflow-x: auto; scrollbar-width: none; padding-bottom: 2px; }
      .igc-chips::-webkit-scrollbar { display: none; }
      .igc-chip {
        flex: 0 0 auto;
        font-family: var(--font-body, 'Rajdhani', sans-serif);
        font-size: 13px; font-weight: 600;
        color: rgba(255,255,255,0.9);
        background: rgba(255,255,255,0.07);
        border: 1px solid rgba(255,255,255,0.12);
        border-radius: 999px;
        padding: 6px 12px;
        cursor: pointer;
        white-space: nowrap;
        -webkit-tap-highlight-color: transparent;
      }
      .igc-chip:active { background: rgba(0,255,136,0.16); border-color: rgba(0,255,136,0.4); transform: scale(0.96); }
      .igc-input-row { display: flex; align-items: flex-end; gap: 8px; }
      .igc-input {
        flex: 1; min-width: 0;
        resize: none;
        font-family: var(--font-body, 'Rajdhani', sans-serif);
        font-size: 15px; font-weight: 500;
        line-height: 1.3;
        color: #fff;
        background: rgba(20,20,24,0.95);
        border: 1px solid rgba(255,255,255,0.14);
        border-radius: 14px;
        padding: 9px 12px;
        max-height: 96px;
        outline: none;
      }
      .igc-input:focus { border-color: rgba(0,255,136,0.5); }
      .igc-input::placeholder { color: rgba(255,255,255,0.4); }
      .igc-send, .igc-close {
        flex: 0 0 auto;
        width: 40px; height: 40px;
        display: grid; place-items: center;
        border-radius: 50%;
        border: 1px solid rgba(255,255,255,0.14);
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }
      .igc-send { background: rgba(0,255,136,0.16); border-color: rgba(0,255,136,0.4); color: #00FF88; }
      .igc-send:active { transform: scale(0.92); }
      .igc-send svg { width: 20px; height: 20px; }
      .igc-close { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.7); }
      .igc-close svg { width: 18px; height: 18px; }

      /* ── History modal ── */
      .igc-history {
        position: fixed; inset: 0; z-index: 695;
        background: rgba(0,0,0,0.6);
        display: flex; align-items: flex-end; justify-content: center;
        opacity: 0; transition: opacity 0.2s ease;
        /* #ui-container is pointer-events:none (canvas gets input) — opt back in. */
        pointer-events: auto;
      }
      .igc-history.open { opacity: 1; }
      .igc-history__sheet {
        width: min(560px, 100%);
        max-height: 78vh;
        display: flex; flex-direction: column;
        background: linear-gradient(180deg, #14141a 0%, #0b0b0e 100%);
        border: 1px solid rgba(255,255,255,0.08);
        border-bottom: none;
        border-radius: 18px 18px 0 0;
        transform: translateY(100%);
        transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
        padding-bottom: env(safe-area-inset-bottom);
      }
      .igc-history.open .igc-history__sheet { transform: translateY(0); }
      .igc-history__header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 16px 18px 10px;
      }
      .igc-history__title {
        font-family: var(--font-display, 'Bebas Neue', sans-serif);
        font-size: 20px; letter-spacing: 2px; color: #fff;
      }
      .igc-history__close {
        width: 32px; height: 32px; display: grid; place-items: center;
        background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
        border-radius: 50%; color: rgba(255,255,255,0.7); cursor: pointer;
      }
      .igc-history__close svg { width: 16px; height: 16px; }
      .igc-history__list {
        flex: 1; overflow-y: auto;
        padding: 6px 16px 18px;
        display: flex; flex-direction: column; gap: 12px;
      }
      .igc-history__empty {
        text-align: center; color: rgba(255,255,255,0.4);
        font-family: var(--font-body, 'Rajdhani', sans-serif);
        padding: 32px 0;
      }
      .igc-history__row { display: flex; gap: 10px; align-items: flex-start; }
      .igc-history__avatar {
        flex: 0 0 auto; width: 34px; height: 34px;
        border-radius: 50%; overflow: hidden;
        background: rgba(255,255,255,0.08);
        display: grid; place-items: center;
        font-family: var(--font-display, 'Bebas Neue', sans-serif);
        font-size: 16px; color: rgba(255,255,255,0.8);
      }
      .igc-history__avatar img { width: 100%; height: 100%; object-fit: cover; }
      .igc-history__body { min-width: 0; flex: 1; }
      .igc-history__meta { display: flex; align-items: baseline; gap: 8px; }
      .igc-history__name {
        font-family: var(--font-body, 'Rajdhani', sans-serif);
        font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.85);
      }
      .igc-history__row.mine .igc-history__name { color: #00FF88; }
      .igc-history__time { font-size: 11px; color: rgba(255,255,255,0.4); }
      .igc-history__text {
        font-family: var(--font-body, 'Rajdhani', sans-serif);
        font-size: 14px; font-weight: 500; line-height: 1.35;
        color: rgba(255,255,255,0.92);
        word-break: break-word; white-space: pre-wrap;
      }
    `;
    document.head.appendChild(style);
  }
}

// ---------- helpers ----------

function formatTime(ts: number): string {
  const d = new Date(ts);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${ampm}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}
